/**
 * API Client for Healthkon PulmoCare Backend
 * 
 * Provides centralized HTTP client with authentication, error handling, and type safety
 */

// ============================================================
// 🔧 BACKEND CONFIGURATION - CHANGE THIS TO TEST YOUR BACKEND
// ============================================================
// 
// Option 1: Use your remote server (RECOMMENDED for testing in Figma)
const BACKEND_URL = 'https://spectrum-api.healthkon.com/pulmo/api/v1';
//const BACKEND_URL = 'http://localhost:8000/api/v1';
//
// Option 2: Use localhost (when running backend locally)
// const BACKEND_URL = 'http://localhost:8004/api/v1';
//
// Option 3: Use environment variable (automatically set from .env file)
// const BACKEND_URL = null; // Will read from .env file
//
// ============================================================

// Cached API base URL
let cachedApiBaseUrl: string | null = null;
let hasLoggedUrl = false;

// Safely access environment variables with fallback - called lazily
const getApiBaseUrl = (): string => {
  // Return cached value if already computed
  if (cachedApiBaseUrl !== null) {
    return cachedApiBaseUrl;
  }

  // PRIORITY 1: Use hardcoded BACKEND_URL if set
  if (BACKEND_URL) {
    cachedApiBaseUrl = BACKEND_URL;
    hasLoggedUrl = true;
    return cachedApiBaseUrl;
  }

  // PRIORITY 2: Try to get from environment variable (Vite will replace this at build time)
  const envUrl = import.meta.env?.VITE_API_BASE_URL;
  
  // Log for debugging only once
  if (!hasLoggedUrl) {
    if (import.meta.env?.DEV) {
      console.log('🔍 Environment Check:', {
        VITE_API_BASE_URL: envUrl || 'not set',
        mode: import.meta.env?.MODE || 'unknown',
        isDev: import.meta.env?.DEV || false
      });
    }
    
    if (envUrl) {
      console.log('✅ Using API URL from .env:', envUrl);
      cachedApiBaseUrl = envUrl;
    } else {
      cachedApiBaseUrl = 'http://localhost:8000/api/v1';
    }
    
    hasLoggedUrl = true;
  }
  
  return cachedApiBaseUrl;
};

interface ApiError {
  detail: string;
  status_code: number;
  errors?: any[];
}

class ApiClient {
  private baseUrl: string | null = null;

  // Lazy getter for baseUrl
  private getBaseUrl(): string {
    if (this.baseUrl === null) {
      this.baseUrl = getApiBaseUrl();
    }
    return this.baseUrl;
  }

  /**
   * Get authentication token from localStorage
   */
  private getToken(): string | null {
    return localStorage.getItem('access_token');
  }

  /**
   * Get default headers with authentication
   */
  private getHeaders(customHeaders: Record<string, string> = {}): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...customHeaders,
    };

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Handle API response
   */
  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const error: ApiError = await response.json().catch(() => ({
        detail: 'An unknown error occurred',
        status_code: response.status,
      }));

      // ✅ ENHANCED: Log 401 errors for debugging
      if (response.status === 401) {
        console.error('❌ API Client - 401 Unauthorized:', {
          url: response.url,
          status: response.status,
          error,
          hasToken: !!this.getToken()
        });
        
        // Try to refresh token
        const refreshed = await this.refreshToken();
        if (!refreshed) {
          console.warn('⚠️ API Client - Token refresh failed, clearing auth data');
          // Clear auth data and redirect to login
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
        }
      }

      // Create a custom error with response data attached
      const customError = new Error(error.detail || 'Request failed') as Error & {
        response?: {
          data: ApiError;
          status: number;
        };
        status_code?: number;
      };
      
      // Attach response data for easier access in catch blocks
      customError.response = {
        data: error,
        status: response.status,
      };
      customError.status_code = response.status;

      throw customError;
    }

    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  /**
   * Refresh access token
   */
  private async refreshToken(): Promise<boolean> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return false;

    try {
      const response = await fetch(`${this.getBaseUrl()}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);
        return true;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
    }

    return false;
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, params?: Record<string, any>): Promise<T> {
    const url = new URL(`${this.getBaseUrl()}${endpoint}`);
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: this.getHeaders(),
      });

      return this.handleResponse<T>(response);
    } catch (error: any) {
      // Handle network errors with better messages
      if (error.message === 'Failed to fetch') {
        throw new Error(
          'Cannot connect to server. Please ensure the backend API is running at ' + this.getBaseUrl()
        );
      }
      throw error;
    }
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, data?: any): Promise<T> {
    try {
      const response = await fetch(`${this.getBaseUrl()}${endpoint}`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(data),
      });

      return this.handleResponse<T>(response);
    } catch (error: any) {
      // Handle network errors with better messages
      if (error.message === 'Failed to fetch') {
        throw new Error(
          'Cannot connect to server. Please ensure the backend API is running at ' + this.getBaseUrl()
        );
      }
      throw error;
    }
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, data?: any): Promise<T> {
    try {
      const response = await fetch(`${this.getBaseUrl()}${endpoint}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(data),
      });

      return this.handleResponse<T>(response);
    } catch (error: any) {
      // Handle network errors with better messages
      if (error.message === 'Failed to fetch') {
        throw new Error(
          'Cannot connect to server. Please ensure the backend API is running at ' + this.getBaseUrl()
        );
      }
      throw error;
    }
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string): Promise<T> {
    try {
      const response = await fetch(`${this.getBaseUrl()}${endpoint}`, {
        method: 'DELETE',
        headers: this.getHeaders(),
      });

      return this.handleResponse<T>(response);
    } catch (error: any) {
      // Handle network errors with better messages
      if (error.message === 'Failed to fetch') {
        throw new Error(
          'Cannot connect to server. Please ensure the backend API is running at ' + this.getBaseUrl()
        );
      }
      throw error;
    }
  }

  /**
   * Upload file
   */
  async upload<T>(endpoint: string, file: File, additionalData?: Record<string, any>): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, String(value));
      });
    }

    const token = this.getToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.getBaseUrl()}${endpoint}`, {
      method: 'POST',
      headers,
      body: formData,
    });

    return this.handleResponse<T>(response);
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

// Export types
export type { ApiError };